import { useEffect, useCallback, useRef } from 'react';
import hotkeys from 'hotkeys-js';

/**
 * Global keyboard shortcuts system.
 *
 * Usage:
 *   useKeyboard({
 *     'command+k': () => openCommandPalette(),
 *     'command+n': () => openQuickCapture(),
 *     '1': () => navigate('today'),
 *   });
 */
export function useKeyboard(bindings, deps = []) {
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  useEffect(() => {
    // Allow shortcuts when input/textarea is focused for global shortcuts
    hotkeys.filter = function(event) {
      const tag = event.target.tagName;
      // Block single-key shortcuts in inputs, allow modifier combos
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return event.metaKey || event.ctrlKey || event.key === 'Escape';
      }
      return true;
    };

    const keys = Object.keys(bindingsRef.current).join(',');
    if (!keys) return;

    hotkeys(keys, (event, handler) => {
      const key = handler.key;
      const fn = bindingsRef.current[key];
      if (fn) {
        event.preventDefault();
        fn(event);
      }
    });

    return () => {
      Object.keys(bindingsRef.current).forEach(key => {
        hotkeys.unbind(key);
      });
    };
  }, deps);
}
